import React, {forwardRef, useContext, useEffect, useImperativeHandle,  useRef, useState} from "react";
import {Button, Divider, Layout, List, message, Modal, Space, Tabs, Tooltip} from "antd";
import {delay, find, pick} from "lodash";
import ChooseAdvertiser from "./ChooseAdvertiser";
import TemplateSearch from "../mccSearch/TemplateSearch";
import SpecAdRender from "../render/SpecAdRender";
import {ClientBridge} from "../index";
import "./SimpleWizard.css";
import {CompanyInfo} from "../uiUtils/CompanyInfo";
import * as PropTypes from "prop-types";
import {AodIcon} from "../AodUi/AodIcon";
import {ArrowUpOutlined, ExclamationCircleOutlined, MinusSquareOutlined} from "@ant-design/icons";
import {PublicImage} from "../uiUtils/Helpers";
import AodBreadcrumb from "../AodUi/AodBreadcrumb";
import {useAodSearch} from "../AodUi/aodHooks";
import {AppController} from "../App";
import {useModel} from "../model/modelHooks";


const { TabPane }= Tabs;

const stepButtonStyle = {width:'100%', marginTop:5};

function StepButton(
    {
        step, activeStep, setStep,
        aodType='', style,
        title,
        doOrSave,
        ...props
    })
{
    const disabled = step===activeStep;
    const goStep = ()=>setStep(activeStep);
    const onClick = doOrSave ? ()=>doOrSave(goStep) : goStep;

    return (
        <Tooltip title={title} className={'step-button'}>
            <Button
                disabled={disabled}
                icon={<AodIcon type={aodType} />}
                onClick={onClick}
                style={{...stepButtonStyle, ...style}}
                {...props}
            />
        </Tooltip>
    );
}
StepButton.propTypes = {
    step: PropTypes.string,
    activeStep: PropTypes.string,
    aodType: PropTypes.string,
    setStep: PropTypes.func
};

function SimpleWizard(
    {
        goToSalesSheet,
        returnToSalesSheet,
        showSideBarInfo = false,
       ...props
    }, ref)
{
    const {model, wizard} = useContext(ClientBridge);
    const controller = useContext(AppController);
    const [step, setStep] = useState("1");  //Note string
    const renderRef = useRef();

    const [template, setTemplate] = useState();
    const [advertiser, setAdvertiser] = useState();
    const [salesSheet, setSalesSheet] = useState();
    //const [salesSheet, setSalesSheet] = useModelItem('salesSheet', 'wizard');
    const [specAd, setSpecAd] = useState();
    const [logo, setLogo] = useState() ;  // todo ? move to render
    const [lastRenderInfo, setLastRenderInfo] = useState({count: 0});
    const [renderHash, setRenderHash] = useState();

    const [crumbs, setCrumbs] = useState([]);
    const account = useModel(model, 'account', 'selected_account', {one: true});

    const [query, setQuery] = useState({});
    const advSsResults = useAodSearch(query,'salesSheet');
    //const { data:{records: advSsList}={}, isSettled} = advSsResults;
    const { data:{records: advSsList=[], total:advSsTotal=0}={}, setData: setAdvSs} = advSsResults;
    const [resetTickler, setResetTickler] = useState(0) ;

    //const unSavedChanges = !specAd || specAd.renderInfo.renderHash!==renderHash;

    useEffect(()=>{
        if (advertiser ) {
            setQuery({
                searchBy: 'advertiser',
                id: advertiser.id
            });
        }
    },[advertiser, resetTickler]);


    useImperativeHandle(ref, () => ({
        resetWizard,
        loadTemplate: renderRef.current.loadTemplate
    }));

    // todo - needs to become imperative action - could have stale advertiser
    useEffect(() => {
        if (template) {
            template.name = template.file_base;  // for display in WizTab
            setStep(advertiser ? '3': '1');
        }
    }, [template]);

    useEffect(()=>{

        const breadCrumbs = [];

        if (account) {
            breadCrumbs.push({
                type: 'account',
                name: account.name,
                onClick: () => goToSaved()
            });

            if (advertiser) {
                breadCrumbs.push({
                    type: 'advertiser',
                    name: advertiser.name,
                    onClick: () => goToSaved({advertiser})
                });

                if (salesSheet) {
                    breadCrumbs.push({
                        type: 'salesSheet',
                        name: salesSheet.name,
                        onClick: () => goToSaved({advertiser, salesSheet})
                    });

                    if (specAd) {
                        breadCrumbs.push({
                            type: 'specAd',
                            name: specAd.jobNumber || specAd.metroReference,
                            //onClick: // todo ?spec Ad pulldown
                        });
                    }
                }
            }
        }

        setCrumbs(breadCrumbs);

    },[advertiser, account, salesSheet, specAd, template, renderHash]);
    // Need template & renderHash to ensure values used in doOrSave not stale
    // extra redraws - maybe be a better solution

    async function resetWizard({salesSheet, specAd, advertiser, template}={}){

        let newStep = '1';

        if (salesSheet) {
            // these checks are required as other users could have changed/deleted SS or SA. todo Adv
            const ssAdv = await controller.getAdvertiser(salesSheet.advertiserId);
            if (!ssAdv) {
                message.error(`Can't open "${salesSheet.name}". (advertiser "${salesSheet.advertiserName}" not found.)`);
                return;
            }
            const freshSalesSheet = await controller.getSalesSheet(salesSheet.id);
            if (!freshSalesSheet) {
                message.error(`Can't open "${salesSheet.name}". (presentation not found.)`);
                return;
            }
            if (specAd && ! find(freshSalesSheet.specAds, {id:specAd.id})){
                const label = specAd.jobNumber || specAd.metroReference;
                message.error(`Can't open "${label}". (not in presentation "${salesSheet.name}".)`);
                return;
            }

            setAdvertiser(ssAdv);
            setSalesSheet(freshSalesSheet);
            newStep = '2';
        } else {
            if (advertiser) {
                setAdvertiser(advertiser);
                newStep = '2';
            } else {
                setAdvertiser();
                setLogo();
            }
            setSalesSheet();
        }

        if (specAd  || (template && newStep==='2')) {
            newStep='3';
        }

        // Allow useEffect [advertiser] in SpecAdRender to fire before we possibly reset adContent
        delay(()=>renderRef.current.resetRender({specAd, template}));
        /*
        delay(()=>runWhen(
            ()=>renderRef.current,
            ()=>renderRef.current.resetRender({specAd, template}),
            0
        ))
         */
        setStep(newStep);
        setResetTickler(resetTickler+1);
    }

    const runWhen = (test, action, delayBy=100) =>
        test() ? action : delay(()=>runWhen(test, action), delayBy)

    function doOrSave(doFunc) {
        if (advertiser && template &&
            (!specAd || specAd.renderInfo.renderHash!==renderHash) ) {
            Modal.confirm({
                title: specAd ? 'Spec Ad changes' : 'New Spec Ad',
                icon: <ExclamationCircleOutlined/>,
                content: 'Do you want to save this spec ad?',
                okText: 'Yes',
                onOk: ()=>renderRef.current.save(),
                cancelText: 'No',
                onCancel: doFunc
            });
        } else {
            doFunc();
        }
    }

    function goToSaved(...args) {
        doOrSave(()=>goToSalesSheet(...args));
    }

    const onSelectTemplate = asset => {
        if (asset===template) {
            setStep('3');
        } else {
          renderRef.current.loadTemplate(asset);
        }
    }

    const onSelectAdvertiser = newAdvertiser => {
        if (salesSheet && salesSheet.id !== advertiser.id){
            setSalesSheet();
            setSpecAd();
        }
        setAdvertiser(newAdvertiser);
    }

    //<Button onClick={returnToSalesSheet}
    return (
        <>
            <Space>
                <Button onClick={()=>goToSaved({advertiser, salesSheet})}
                        style={{width:190,marginLeft:5, paddingLeft: 5, paddingRight: 5}}
                        type={'primary'}
                        icon={<AodIcon type={'salesSheet'} />}
                    >Saved Presentations</Button>
                <AodBreadcrumb crumbs={crumbs} clickLast={true} style={{marginTop:0}}/>
            </Space>
            <Layout className={'simple-wizard'} >
            <Layout.Sider theme={'light'} style={{padding:5}}>
                <div style={{height:150}}>
                    <h1 style={{fontSize:36, marginBottom: -7}}>STEP {step}</h1>
                    <h3 style={{fontStyle:'italic',color:'red', fontSize:20, lineHeight: 1.2}}>{
                        step === '1' ? 'Choose or create an advertiser.' :
                        step === '2' ? 'Click on a template to see your spec ad.' :
                        step === '3' ? <span>Presto!<br/>Share, download or further customize your spec ad.</span>
                            : ''
                    }</h3>
                </div>

                <Divider style={{margin:0, borderColor:'lightgray'}}/>

                <List>
                    {advertiser && <SidebarItem>
                        <CompanyInfo
                            company={showSideBarInfo
                                ? advertiser.company
                                : pick(advertiser.company, 'name', 'address1')}
                            alternateName={advertiser.name}
                        />
                        <StepButton step={step} activeStep='1' aodType='advertiser'
                                    doOrSave={doOrSave} setStep={setStep}
                                    title={"Return to Step 1"}
                        >Change Advertiser</StepButton>
                    </SidebarItem>}

                    {template && <SidebarItem text={template.name}>
                        <StepButton step={step} activeStep='2' aodType='template'
                                    setStep={setStep} doOrSave={doOrSave}
                                    title={"Return to Step 2"}
                        >Change Template</StepButton>
                    </SidebarItem>}

                    { (specAd || template && step!=='3') &&
                    <SidebarItem text={showSideBarInfo && (
                        specAd ? (specAd.jobNumber || specAd.metroReference) : 'unsaved spec ad')}>
                        <StepButton step={step} activeStep='3' aodType='specAd'
                                    setStep={setStep}
                                    title={"Return to Step 3"}
                        >Share/Download Ad</StepButton>
                    </SidebarItem>}

                    {salesSheet && <SidebarItem text={showSideBarInfo && salesSheet.name}>
                        <Button
                            icon={<AodIcon type={'salesSheet'} />}
                            onClick={()=>goToSaved({advertiser, salesSheet})}
                            style={stepButtonStyle}
                            {...props}
                        >
                        Open Presentation</Button>
                    </SidebarItem>}

                    {(advertiser || template) && <List.Item>
                        <Tooltip title={"Return to Step 1 & clear all"}>
                            <Button icon={<MinusSquareOutlined/>}
                                    onClick={()=>doOrSave(resetWizard)}
                                    title={"Return to Step 1"}
                                    style={{width:190}}
                            >Start Over</Button>
                        </Tooltip>
                    </List.Item>}

                    {wizard.isAdSeller && <List.Item>
                        <Button icon={<ArrowUpOutlined rotate={45} />}
                                style={stepButtonStyle}
                                onClick={()=>doOrSave(()=>wizard.adSellerReturn())}
                                > Return to Pulse</Button>
                    </List.Item>}

                    {step==='2' && !template &&
                        <PublicImage path={'wizard-step-2.jpg'} alt={'fwizard'}
                                 style={{marginLeft:-5}}/>}
                </List>
            </Layout.Sider>

            <Layout.Content>
                <Tabs tabBarStyle={{display:'none'}} {...props}  activeKey={step}>

                    <TabPane key={'1'} tab={'Advertiser'} style={{width:1050}}>
                            <ChooseAdvertiser style={{width:600, marginTop:10, display:'inline-block'}}
                                advertiser={advertiser} onSelect={onSelectAdvertiser}
                                logo={logo} setLogo={setLogo}
                                next={()=>setStep(template?'3':'2')}
                                advSsTotal={advSsTotal}
                            />
                            <PublicImage path={'wizard-step-1.jpg'} alt={'fwizard'}
                                     style={{
                                         width:300,
                                         display:'inline-block',
                                         verticalAlign: 'top',
                                         margin: 10
                                     }}/>
                    </TabPane>

                    <TabPane key={'2'} tab={'Template'}>
                        <TemplateSearch onSelect={onSelectTemplate} advertiser={advertiser}/>
                    </TabPane>

                    <TabPane key={'3'} tab={'Spec Ad'} forceRender>
                        <SpecAdRender template={template} setTemplate={setTemplate}
                                      advertiser={advertiser} advSsResults={advSsResults}
                                      salesSheet={salesSheet} setSalesSheet={setSalesSheet}
                                      specAd={specAd} setSpecAd={setSpecAd}
                                      renderHash={renderHash} setRenderHash={setRenderHash}
                                      doOrSave={doOrSave}
                                      visible={step === "3"}
                                      ref={renderRef}

                                      lastRenderInfo={lastRenderInfo}  setLastRenderInfo={setLastRenderInfo}
                                      logo={logo || {}} setLogo={setLogo}
                        />
                    </TabPane>
                </Tabs>
            </Layout.Content>

        </Layout>
        </>
    );
}
export default SimpleWizard = forwardRef(SimpleWizard);

function SidebarItem({text, children, style, ...props}){

    return <List.Item {...props} style={{borderColor:'lightgray', ...style}}>
            <div style={{width:'100%'}}>
                {text && <div style={{textAlign:'center'}}>
                    <span style={{fontWeight: 'bold'}}>{text}</span>
                </div>}
                {children}
            </div>
        </List.Item>
}


// Spec Ad thimbnail in side bae
// todo - prefs to show thumbnails - all pulled out for now
/*
{lastRenderInfo.thumbUrl && <List.Item><div style={{width:'100%', textAlign:'center'}}>
                        <img src={lastRenderInfo.thumbUrl} alt={'Spec ad'} style={{maxWidth:150,maxHeight:150, margin:'auto'}}/>
                        <StepButton step={step} activeStep='3' aodType='specAd' setStep={setStep}>Share/download Ad</StepButton>
                    </div></List.Item>}
 */